// Print the order sheet straight from the editor, through the browser's own
// print dialog (printer or "Save as PDF") - no save or .docx round trip.
//
// The sheet is rendered alone in a hidden iframe so the dashboard around the
// editor does not print, and so none of the app's global CSS (Analytics.css
// restyles every <table>) reaches the sheet. The iframe is sandboxed without
// allow-scripts: the HTML is only laid out and printed, never executed.

// Mirrors the content rules of editor.css + orderSheet.css, which only apply
// inside the editor's DOM. The side margins live on @page (not body padding)
// so the running header's margin boxes line up with the text.
const PRINT_CSS = `
  @page { size: A4; margin: 15mm 0.6in; }
  html, body { margin: 0; background: #fff; }
  /* No bottom padding: @page already provides the margin, and extra space
     after the last line only pushes a sheet that just fits onto a blank
     extra page. */
  body {
    padding: 2pt 0 0;
    font-size: 14px;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  img { max-width: 100%; }
  table {
    border-collapse: collapse;
    width: auto;
    max-width: 100%;
    height: auto;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }
  td, th { padding: 1px; border: 0; vertical-align: top; }
  table[border] { table-layout: fixed; width: 100%; }
  table[border] td, table[border] th {
    border: 1px solid #000;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  /* Keep the letterhead in one piece, but let every other table's rows split:
     the order table's single row holds the whole order body, so forbidding a
     split pushed it to page 2 and left page 1 mostly blank. The letterhead is
     the table paginate() marks, not "any table without a border": an order
     table that reaches print without border="1" (its lines drawn by inline
     cell styles) would otherwise be held together the same way. */
  table[data-letterhead] tr { page-break-inside: avoid; break-inside: avoid; }
  /* Chrome will not cut a row where all that would carry over is the cell's
     bottom padding: rather than that, it moves the whole order table to the
     next page and leaves page 1 blank under the heading. It happens when
     the sheet only just overflows the page - a short order padded out with
     blank lines. Giving each piece of a cut cell its own padding lets Chrome
     cut it instead, and dropping the last block's bottom margin (blank space
     after the last line) lets a sheet that only just fits stay on its page. */
  table[border] td, table[border] th {
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
  }
  table[border] td > :last-child, table[border] th > :last-child {
    margin-bottom: 0;
  }
  /* The editor always keeps an empty paragraph after a final table; printed,
     its margins alone can start a blank page. */
  body > p:last-child:empty {
    display: none;
  }
  /* The editor writes a page break as "page-break-before: always" on the
     block itself. Chrome still honours that legacy property, but the modern
     alias is what the spec defines, so set both. A break in the order body
     only takes effect because the bordered table's rows are allowed to split
     (see the rule above). */
  [style*="break-before"] {
    page-break-before: always;
    break-before: page;
  }
  /* Every page after the first (see layOutLaterPages): its "Post this
     matter" line and the signature lines are a row of their own at the
     bottom of the order table, with no rule between them and the text. */
  table[border] tr[data-page-text] > td,
  table[border] tr[data-page-text] > th {
    border-bottom: 0;
  }
  table[border] tr[data-page-foot] > td,
  table[border] tr[data-page-foot] > th {
    border-top: 0;
    vertical-align: bottom;
  }
  /* The signature lines never split from each other or from the link. */
  table[border] tr[data-page-foot] { page-break-inside: avoid; break-inside: avoid; }
  /* A table that is all its page holds fills the sheet, the text row taking
     the slack. Just short of the 267mm the @page margins leave, so rounding
     never spills it onto a blank sheet. */
  table[data-fill-sheet] { height: 265mm; }
  table[data-fill-sheet] tr[data-page-foot] { height: 1px; }
  /* The rules that close a table cut by a page end - see closePageEdges. */
  table[border] > [data-page-edge] td {
    height: 0;
    padding: 0;
    border: 0;
    font-size: 0;
    line-height: 0;
  }
  /* The header is a short band - the top rule with a little space under it,
     so text continued from the previous page does not touch the rule. */
  table[border] > thead[data-page-edge] td {
    height: 4pt;
    border-top: 1px solid #000;
    border-left: 1px solid #000;
    border-right: 1px solid #000;
  }
  /* Where the table starts, that band is the top of its first row, so the
     row's own top border would draw a second rule under it. */
  table[border] > thead[data-page-edge] + tbody > tr:first-child > td,
  table[border] > thead[data-page-edge] + tbody > tr:first-child > th {
    border-top: 0;
  }
  table[border] > tfoot[data-page-edge] td { border-top: 1px solid #000; }
  a { color: #0000ff; text-decoration: underline; }
`;

const IMAGE_WAIT_MS = 3000;

// ---------------------------------------------------------------- pagination
// Mirrors bac/order_pages.py, so the printout pages exactly like the .docx.

const BREAK_DECL = /(?:^|;)\s*(?:page-)?break-before\s*:\s*(?:always|page)\s*(?:;|$)/i;

// "page" when the element starts a new page, else null.
const breakKind = (el) => {
  if (!el || el.nodeType !== 1) return null;
  const separator = el.getAttribute("data-page-break");
  if (separator) return separator;
  return BREAK_DECL.test(el.getAttribute("style") || "") ? "page" : null;
};

const clearBreak = (el) => {
  const kept = (el.getAttribute("style") || "")
    .split(";")
    .map((decl) => decl.trim())
    .filter((decl) => decl && !/^(?:page-)?break-before\s*:/i.test(decl));
  if (kept.length) el.setAttribute("style", kept.join("; "));
  else el.removeAttribute("style");
};

const separator = (doc, kind) => {
  const div = doc.createElement("div");
  div.setAttribute("data-page-break", kind);
  div.setAttribute("style", "break-before: page");
  return div;
};

// A break directly inside a cell of `table` (not of a nested table).
const firstBreakInCell = (table) =>
  Array.from(table.querySelectorAll("*")).find((el) => {
    const cell = el.parentElement;
    return (
      breakKind(el) &&
      cell &&
      (cell.tagName === "TD" || cell.tagName === "TH") &&
      cell.closest("table") === table
    );
  });

// Move `block` and everything after it into a copy of `table` that starts on
// the next page - the way a table continues across pages in Word. A page
// break inside one tall cell would otherwise be ignored by the browser.
const splitTable = (doc, table, block) => {
  const cell = block.parentElement;
  const row = cell.parentElement;
  const section = row.parentElement; // <tbody> / <thead> / the <table>
  const kind = breakKind(block);

  const newTable = table.cloneNode(false);
  const newSection = section === table ? newTable : section.cloneNode(false);
  const newRow = row.cloneNode(false);
  Array.from(row.children).forEach((oldCell) => {
    const newCell = oldCell.cloneNode(false);
    if (oldCell === cell) {
      let node = block;
      while (node) {
        const next = node.nextSibling;
        newCell.appendChild(node);
        node = next;
      }
    }
    newRow.appendChild(newCell);
  });
  newSection.appendChild(newRow);
  let later = row.nextElementSibling;
  while (later) {
    const next = later.nextElementSibling;
    newSection.appendChild(later);
    later = next;
  }
  if (section !== table) {
    newTable.appendChild(newSection);
    let laterSection = section.nextElementSibling;
    while (laterSection) {
      const next = laterSection.nextElementSibling;
      newTable.appendChild(laterSection);
      laterSection = next;
    }
  }

  clearBreak(block);
  table.after(separator(doc, kind), newTable);
};

// The ACCB letterhead: the order sheet's first, borderless table with the logo.
const findLetterhead = (body) => {
  const first = body.firstElementChild;
  return first &&
    first.tagName === "TABLE" &&
    !first.hasAttribute("border") &&
    first.querySelector("img")
    ? first
    : null;
};

const paginate = (doc) => {
  const body = doc.body;
  let table;
  do {
    table = Array.from(body.children).find(
      (el) => el.tagName === "TABLE" && firstBreakInCell(el)
    );
    if (table) splitTable(doc, table, firstBreakInCell(table));
  } while (table);

  // Every break as a separator in front of the page it starts.
  Array.from(body.children).forEach((el) => {
    const kind = breakKind(el);
    if (!kind || el.hasAttribute("data-page-break")) return;
    clearBreak(el);
    el.before(separator(doc, kind));
  });

  // For the keep-together rule in PRINT_CSS. Only page 1 has the letterhead;
  // every later page opens under the case-number header (see pageCss).
  const letterhead = findLetterhead(body);
  if (letterhead) letterhead.setAttribute("data-letterhead", "");
};

// A row too tall for the page - the order body is one row - is cut open where
// the page ends: Chrome draws no border at the cut, so the text runs straight
// into the page number below and the case number on the next page. Chrome
// repeats a table's header and footer groups on every page the table touches,
// so an empty pair of them closes each piece with a rule. Where the table
// really starts and ends they stand in for its own top and bottom border.
const closePageEdges = (doc) => {
  Array.from(doc.body.querySelectorAll("table[border]"))
    // Top-level tables only; a table inside a cell is cut with its parent.
    .filter((table) => !table.parentElement.closest("table"))
    // Never displace a header or footer the document already has.
    .filter((table) => table.rows.length && !table.tHead && !table.tFoot)
    .forEach((table) => {
      const first = table.rows[0];
      const edge = (tag) => {
        const group = doc.createElement(tag);
        group.setAttribute("data-page-edge", "");
        const row = doc.createElement("tr");
        // Mirror the first row's columns: with table-layout: fixed the first
        // row sets the column widths, and this one now comes first.
        Array.from(first.cells).forEach((cell) => {
          const blank = doc.createElement("td");
          blank.colSpan = cell.colSpan;
          if (cell.style.width) blank.style.width = cell.style.width;
          if (cell.hasAttribute("width")) {
            blank.setAttribute("width", cell.getAttribute("width"));
          }
          row.appendChild(blank);
        });
        group.appendChild(row);
        return group;
      };
      table.insertBefore(edge("thead"), table.firstChild);
      table.appendChild(edge("tfoot"));
    });
};

// For a CSS string literal.
const cssString = (text) =>
  `"${String(text).replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, " ")}"`;

// --------------------------------------------------------------- later pages
// Every page after the first is laid out like page 1: the order table, the
// page's text at the top of the order column, and its "Post this matter" line
// and the signature lines under it at the bottom of the table, which fills
// the sheet. Page 1 stays as the template has it. bac/order_pages.py does the
// same for the .docx (split_page_foot). A page whose text runs over onto
// another sheet has its line and signature after the text there.

const POST_MATTER = /Post this matter/i;

// The pages paginate() left: runs of the body's elements, each starting at a
// break separator.
const sheetPages = (body) => {
  const pages = [];
  Array.from(body.children).forEach((el) => {
    if (!pages.length || el.hasAttribute("data-page-break")) pages.push([]);
    pages[pages.length - 1].push(el);
  });
  return pages;
};

const layOutLaterPages = (doc) => {
  sheetPages(doc.body).forEach((page, index) => {
    if (!index) return;
    // The order table on this page, and the line in its cell that holds the
    // link: everything from there down is the foot of the page.
    let cell = null;
    let line = null;
    page.some((el) => {
      if (el.tagName !== "TABLE" || !el.hasAttribute("border")) return false;
      const anchor = Array.from(el.querySelectorAll("a[href]")).find((a) =>
        POST_MATTER.test(a.textContent)
      );
      cell = anchor && anchor.closest("td, th");
      if (!cell || cell.closest("table") !== el) return false;
      line = anchor;
      while (line.parentElement !== cell) line = line.parentElement;
      return true;
    });
    if (!line) return;

    const row = cell.parentElement;
    const foot = row.cloneNode(false);
    Array.from(row.children).forEach((rowCell) => {
      const copy = rowCell.cloneNode(false);
      if (rowCell === cell) {
        let node = line;
        while (node) {
          const next = node.nextSibling;
          copy.appendChild(node);
          node = next;
        }
      }
      foot.appendChild(copy);
    });
    row.after(foot);
    row.setAttribute("data-page-text", "");
    foot.setAttribute("data-page-foot", "");

    // The page-break line a New page leaves above its table has nothing to
    // print: the separator starts the page.
    const table = row.closest("table");
    page.forEach((el) => {
      if (
        el.tagName === "P" &&
        !el.textContent.trim() &&
        !el.querySelector("img") &&
        el.compareDocumentPosition(table) & 4 // the table follows it
      ) {
        el.remove();
      }
    });

    // Fill the sheet only when nothing else on the page needs room.
    const alone = page.filter((el) => doc.body.contains(el)).every(
      (el) =>
        el === table ||
        el.hasAttribute("data-page-break") ||
        // The editor's trailing empty paragraph, which does not print.
        (el === doc.body.lastElementChild && el.tagName === "P" && !el.textContent)
    );
    if (alone) table.setAttribute("data-fill-sheet", "");
  });
};

// "AC No. 12/2026" - the case id usually carries the prefix already.
const caseHeaderText = (caseNumber) => {
  const ac = String(caseNumber || "").trim();
  if (!ac) return "";
  return /\bNo\./.test(ac) ? ac : `AC No. ${ac}`;
};

// The case number, bold and centred, at the top of every page but page 1
// (which has the letterhead); the page number at the bottom right of every
// page, starting at `startPage`. Chrome prints its own date / title / URL /
// page count only in margin areas the page leaves empty, so the top box is
// always given content - an empty string where there is no case number.
const pageCss = (caseNumber, startPage, fontFamily) => {
  const start = Math.max(1, parseInt(startPage, 10) || 1);
  const heading = caseHeaderText(caseNumber);
  return `
    html { counter-reset: page ${start - 1}; }
    @page {
      @top-center {
        content: ${cssString(heading)};
        font-family: ${fontFamily};
        font-size: 12pt;
        font-weight: bold;
        vertical-align: bottom;
        /* Clear of the text, and of a cut table's top rule, below it. */
        padding-bottom: 5mm;
      }
      @bottom-right {
        content: "Page " counter(page);
        font-family: ${fontFamily};
        font-size: 10pt;
        vertical-align: top;
        padding-top: 5mm;
      }
    }
    @page :first { @top-center { content: ""; } }
  `;
};

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));

// Wait for the letterhead and any other images, so they are not missing from
// the printout; give up after a few seconds rather than never printing.
const waitForImages = (doc) =>
  Promise.all(
    Array.from(doc.images)
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
            setTimeout(resolve, IMAGE_WAIT_MS);
          })
      )
  );

/**
 * Open the browser print dialog for an order sheet.
 *
 * @param {string} html  The sheet HTML, as held by the editor.
 * @param {string} title Used as the print job / PDF file name.
 * @param {{ caseNumber?: string, startPage?: number }} [options]
 *   caseNumber: the header of every page but page 1.
 *   startPage: the number printed on page 1; later pages continue from it.
 */
export default async function printOrderSheet(
  html,
  title = "Order Sheet",
  { caseNumber = "", startPage = 1 } = {}
) {
  // Print in the font the editor shows the sheet in.
  const shown = document.querySelector(".rte--order .ProseMirror");
  const fontFamily = shown ? window.getComputedStyle(shown).fontFamily : "serif";

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("sandbox", "allow-same-origin allow-modals");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
    visibility: "hidden",
  });
  document.body.appendChild(iframe);

  const remove = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  try {
    const doc = iframe.contentDocument;
    doc.open();
    doc.write(
      `<!doctype html><html><head><meta charset="utf-8">` +
        `<title>${escapeHtml(title)}</title>` +
        `<style>${PRINT_CSS} body { font-family: ${fontFamily}; }` +
        `${pageCss(caseNumber, startPage, fontFamily)}</style>` +
        `</head><body>${html || ""}</body></html>`
    );
    doc.close();

    paginate(doc);
    layOutLaterPages(doc);
    closePageEdges(doc);

    await waitForImages(doc);

    const win = iframe.contentWindow;
    // Chrome blocks in print() until the dialog closes; Firefox returns at
    // once and fires afterprint later. Clean up on whichever comes last.
    win.addEventListener("afterprint", () => setTimeout(remove, 0), { once: true });
    win.focus();
    win.print();
    setTimeout(remove, 60000);
  } catch (err) {
    remove();
    throw err;
  }
}
